import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Navigate, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Layout from './components/layout/Layout';
import WhatsAppButton from './components/common/WhatsAppButton';
import CookieBanner, { COOKIE_CONSENT_KEY } from './components/common/CookieBanner';
import LoadingSpinner from './components/common/LoadingSpinner';
import HomePage from './pages/HomePage';
import AdminRoute from './components/admin/AdminRoute';
import { initGA, trackPageView } from './utils/analytics';
import { captureAttribution } from './utils/attribution';
import './App.css';

const SolutionsPage = lazy(() => import('./pages/SolutionsPage'));
const CaseStudiesPage = lazy(() => import('./pages/CaseStudiesPage'));
const ProductRedirect = lazy(() => import('./pages/ProductRedirect'));
const ToolsListPage = lazy(() => import('./pages/ToolsListPage'));
const ToolDetailPage = lazy(() => import('./pages/ToolDetailPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const CompaniesPage = lazy(() => import('./pages/CompaniesPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const PrivacyNotice = lazy(() => import('./pages/PrivacyNotice'));
const CookiesPolicy = lazy(() => import('./pages/CookiesPolicy'));
const TermsConditions = lazy(() => import('./pages/TermsConditions'));
const ReturnsPolicy = lazy(() => import('./pages/ReturnsPolicy'));
const AwsCostOptimizationLanding = lazy(() => import('./pages/landing/AwsCostOptimizationLanding'));
const ReportAutomationLanding = lazy(() => import('./pages/landing/ReportAutomationLanding'));
const AwsCostCaseStudy = lazy(() => import('./pages/cases/AwsCostCaseStudy'));
const ReportAutomationCaseStudy = lazy(() => import('./pages/cases/ReportAutomationCaseStudy'));
const AdminLoginPage = lazy(() => import('./pages/AdminLoginPage'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const AdminPostsPage = lazy(() => import('./pages/AdminPostsPage'));
const AdminPostCreatePage = lazy(() => import('./pages/AdminPostCreatePage'));
const AdminPostEditPage = lazy(() => import('./pages/AdminPostEditPage'));
const AdminProductsPage = lazy(() => import('./pages/AdminProductsPage'));
const AdminProductCreatePage = lazy(() => import('./pages/AdminProductCreatePage'));
const AdminProductEditPage = lazy(() => import('./pages/AdminProductEditPage'));
const AdminLeadsPage = lazy(() => import('./pages/AdminLeadsPage'));
const AdminCommentsPage = lazy(() => import('./pages/AdminCommentsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    captureAttribution(location.search);
    trackPageView(location.pathname);
    window.scrollTo(0, 0);
  }, [location.pathname, location.search]);

  return null;
}

function AppFrame() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isLandingRoute = [
    '/soluciones/reducir-costos-aws',
    '/soluciones/automatizar-reportes',
  ].includes(location.pathname);

  return (
    <>
      <Layout>
        <Suspense fallback={<LoadingSpinner text="Cargando contenido..." />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/soluciones" element={<SolutionsPage />} />
          <Route path="/soluciones/reducir-costos-aws" element={<AwsCostOptimizationLanding />} />
          <Route path="/soluciones/automatizar-reportes" element={<ReportAutomationLanding />} />
          <Route path="/casos-de-exito" element={<CaseStudiesPage />} />
          <Route path="/casos-de-exito/aws-optimizacion" element={<AwsCostCaseStudy />} />
          <Route path="/casos-de-exito/automatizacion-reportes" element={<ReportAutomationCaseStudy />} />
          <Route path="/productos" element={<Navigate to="/herramientas" replace />} />
          <Route path="/productos/:slug" element={<ProductRedirect />} />
          <Route path="/herramientas" element={<ToolsListPage />} />
          <Route path="/herramientas/:slug" element={<ToolDetailPage />} />
          <Route path="/empresas" element={<CompaniesPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/contacto" element={<ContactPage />} />
          <Route path="/aviso-privacidad" element={<PrivacyNotice />} />
          <Route path="/cookies" element={<CookiesPolicy />} />
          <Route path="/terminos" element={<TermsConditions />} />
          <Route path="/devoluciones" element={<ReturnsPolicy />} />
          <Route path="*" element={<NotFoundPage />} />
          <Route path="/gestion-de-contenido" element={<Navigate to="/admin/login" replace />} />
          <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route
            path="/admin/dashboard"
            element={(
              <AdminRoute>
                <AdminDashboardPage />
              </AdminRoute>
            )}
          />
          <Route
            path="/admin/posts"
            element={(
              <AdminRoute>
                <AdminPostsPage />
              </AdminRoute>
            )}
          />
          <Route
            path="/admin/posts/new"
            element={(
              <AdminRoute>
                <AdminPostCreatePage />
              </AdminRoute>
            )}
          />
          <Route
            path="/admin/posts/:id/edit"
            element={(
              <AdminRoute>
                <AdminPostEditPage />
              </AdminRoute>
            )}
          />
          <Route
            path="/admin/products"
            element={(
              <AdminRoute>
                <AdminProductsPage />
              </AdminRoute>
            )}
          />
          <Route
            path="/admin/products/new"
            element={(
              <AdminRoute>
                <AdminProductCreatePage />
              </AdminRoute>
            )}
          />
          <Route
            path="/admin/products/:id/edit"
            element={(
              <AdminRoute>
                <AdminProductEditPage />
              </AdminRoute>
            )}
          />
          <Route
            path="/admin/leads"
            element={(
              <AdminRoute>
                <AdminLeadsPage />
              </AdminRoute>
            )}
          />
          <Route
            path="/admin/comments"
            element={(
              <AdminRoute>
                <AdminCommentsPage />
              </AdminRoute>
            )}
          />
        </Routes>
        </Suspense>
      </Layout>
      {!isAdminRoute && !isLandingRoute && <WhatsAppButton />}
      {!isAdminRoute && <CookieBanner />}
    </>
  );
}

function App() {
  useEffect(() => {
    const initializeAnalyticsFromConsent = (event) => {
      if (event?.detail?.accepted) initGA();
    };

    try {
      const savedConsent = JSON.parse(window.localStorage.getItem(COOKIE_CONSENT_KEY));
      if (savedConsent?.accepted) initGA();
    } catch (error) {
      // Keep analytics disabled when consent cannot be read.
    }

    window.addEventListener('creditoolsmx:cookie-consent', initializeAnalyticsFromConsent);
    return () => window.removeEventListener('creditoolsmx:cookie-consent', initializeAnalyticsFromConsent);
  }, []);

  return (
    <HelmetProvider>
      <BrowserRouter>
        <PageViewTracker />
        <AppFrame />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
